import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, Calendar, Award, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HallOfFame() {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const response = await axios.get(`${API}/hall-of-fame`);
        setWinners(response.data);
      } catch (err) {
        logger.error("Error fetching hall of fame:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchWinners();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-casino-black">
        <div className="text-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="hall-of-fame-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-casino-black to-casino-dark relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: "url('https://images.pexels.com/photos/6532369/pexels-photo-6532369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-casino-dark" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <Trophy className="w-20 h-20 text-gold mx-auto mb-8" />
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Hall of <span className="gold-gradient">Fame</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            Celebrating our tournament champions and their victories at San Marcos Social Club.
          </p>
        </div>
      </section>

      {/* Winners Grid */}
      <section className="py-20 bg-casino-black" data-testid="winners-section">
        <div className="max-w-7xl mx-auto px-4">
          {winners.length === 0 ? (
            <div className="text-center py-20">
              <Award className="w-16 h-16 text-gold/30 mx-auto mb-4" />
              <p className="text-white/60 text-lg">No winners recorded yet. Be the first champion!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {winners.map((winner, index) => (
                <div 
                  key={winner.id}
                  className="glass-card overflow-hidden"
                  data-testid={`winner-card-${index}`}
                >
                  {/* Photo or Placeholder */}
                  <div className="aspect-video bg-gradient-to-br from-gold/20 to-casino-dark relative overflow-hidden">
                    {winner.player_photo ? (
                      <img 
                        src={winner.player_photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${winner.player_photo}` : winner.player_photo}
                        alt={winner.player_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-20 h-20 text-gold/30" />
                      </div>
                    )}
                    <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-gold flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-black" />
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-6">
                    <h3 className="font-heading text-2xl text-white mb-2">
                      {winner.player_name}
                    </h3>
                    <p className="text-gold font-medium mb-3">{winner.tournament_name}</p>
                    <div className="flex items-center gap-2 text-white/50 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>{winner.date}</span>
                    </div>
                    {winner.prize && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <span className="text-gold font-semibold">{winner.prize}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-casino-dark">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            Ready to Make History?
          </h2>
          <p className="text-white/60 mb-8">
            Join our tournaments and see your name on the Hall of Fame!
          </p>
          <a
            href="/tournaments"
            className="inline-block bg-gold text-black font-bold uppercase tracking-wider px-8 py-4 text-sm hover:bg-gold-dark transition-all duration-300 hover:scale-105 shadow-gold"
            data-testid="cta-tournaments"
          >
            View Tournament Schedule
          </a>
        </div>
      </section>
    </div>
  );
}
